<template>
  <div class="page-container">
    <div class="header-section">
      <h4 class="page-title">Calculate your savings with Renox AI</h4>
      <p class="page-description">
        Enter your home address and see how much you could save by letting Renox AI manage your solar and battery smarter.
      </p>
    </div>

    <div class="main-content">
      <!-- Plant Selection Section -->
      <div class="plant-selection-section">
        <div class="plant-selection-card">
          <div class="plant-header">
            <h5>Enter Your Address</h5>
            <p>Type your address to analyse your profile</p>
          </div>
          <div class="plant-search-container">
            <div class="search-input-wrapper">
              <input
                v-model="plantSearchInput"
                @input="onPlantSearchInput"
                @keydown="onPlantSearchKeydown"
                @focus="showPlantDropdown = true"
                @blur="hidePlantDropdown"
                type="text"
                placeholder="Type here..."
                class="plant-search-input"
                :class="{ 'error': plantSearchError }"
              />
              <div v-if="isSearchingPlants" class="search-spinner"></div>
            </div>
            
            <!-- Plant Dropdown -->
            <div v-if="showPlantDropdown && plantList.length > 0" class="plant-dropdown">
              <div
                v-for="(plant, index) in plantList"
                :key="plant.plant_id"
                @click="selectPlant(plant)"
                class="plant-option"
                :class="{ 'highlighted': index === selectedPlantIndex }"
              >
                <div class="plant-info">
                  <div class="plant-name">{{ plant.plant_name }}</div>
                  <div class="plant-account">{{ plant.account_name }}</div>
                </div>
                <div class="plant-id">ID: {{ plant.plant_id }}</div>
              </div>
            </div>
            
            <!-- No Results -->
            <div v-if="showPlantDropdown && plantList.length === 0 && plantSearchInput.length >= 3" class="no-results">
              No plants found matching "{{ plantSearchInput }}"
            </div>
          </div>
          
          <!-- Selected Plant Display -->
          <div v-if="selectedPlant" class="selected-plant">
            <div class="selected-plant-info">
              <h5>Entered Address</h5>
              <div class="plant-details">
                <span class="plant-name">{{ selectedPlant.plant_name }}</span>
                <span class="plant-account">({{ selectedPlant.account_name }})</span>
                <span class="plant-id">ID: {{ selectedPlant.plant_id }}</span>
              </div>
            </div>
            <button @click="clearSelectedPlant" class="clear-plant-btn">×</button>
          </div>
          
          <!-- Search Error -->
          <div v-if="plantSearchError" class="search-error">
            {{ plantSearchError }}
          </div>
          
          <!-- Calculate Button -->
          <div v-if="!showResults && !showSystemInfo && !showEstimatedBill" class="calculate-button-container">
            <button 
              class="calculate-button" 
              @click="calculateSavings"
              :disabled="isLoading || !selectedPlant"
            >
              <span v-if="isLoading" class="loading-spinner"></span>
              {{ isLoading ? 'Calculating...' : 'Calculate My Savings with Renox AI' }}
            </button>
            <p v-if="!selectedPlant" class="selection-required">
              Please enter the address first
            </p>
          </div>
        </div>
      </div>

      <!-- System Information Confirmation Modal -->
      <div v-if="showSystemInfo && systemInfo" class="modal-overlay" @click.self="editSystemInfo">
        <div class="system-info-modal">
          <button class="modal-close" @click="editSystemInfo">×</button>
          
          <div class="system-info-header">
            <h3>Confirm Your System Information</h3>
            <p>Please review the information below and confirm it's accurate before we calculate your savings.</p>
            <div v-if="systemInfo.info_message_for_user && systemInfo.info_message_for_user.trim() !== ''" class="info-message-container">
              <div class="info-message-divider"></div>
              <p class="info-message-for-user">
                {{ systemInfo.info_message_for_user }}
              </p>
            </div>
          </div>

          <div class="system-info-grid">
            <div class="info-item">
              <div class="info-label">Estimated PV Array Size</div>
              <div class="info-input-wrapper">
                <input
                  v-model="pvSizeUserProvided"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="Enter PV array size"
                  class="info-input"
                  @input="modalErrorMessage = ''"
                />
                <span class="info-unit">kW</span>
              </div>
              <div class="info-hint">You can edit this value if needed</div>
            </div>
          </div>

          <div v-if="modalErrorMessage" class="modal-error-message">
            {{ modalErrorMessage }}
          </div>

          <div class="system-info-actions">
            <button 
              class="edit-button" 
              @click="editSystemInfo"
              :disabled="isLoading"
            >
              Edit / Go Back
            </button>
            <button 
              class="confirm-button" 
              @click="proceedWithCalculation"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="loading-spinner"></span>
              {{ isLoading ? 'Calculating...' : 'Confirm & Calculate Savings' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Estimated Bill Confirmation Modal -->
      <div v-if="showEstimatedBill" class="modal-overlay" @click.self="editEstimatedBill">
        <div class="system-info-modal">
          <button class="modal-close" @click="editEstimatedBill">×</button>
          
          <div class="system-info-header">
            <h3>Confirm Your Estimated Monthly Bill</h3>
            <p>Please review and confirm your estimated monthly bill amount before we calculate your savings.</p>
          </div>

          <div class="system-info-grid">
            <div class="info-item">
              <div class="info-label">Our estimate of your current monthly bill</div>
              <div class="info-input-wrapper">
                <input
                  v-model="estimatedBillUserProvided"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Enter estimated monthly bill"
                  class="info-input"
                  @input="modalErrorMessage = ''"
                />
                <span class="info-unit">$</span>
              </div>
              <div class="info-hint">You can edit this value if needed</div>
            </div>
          </div>

          <div v-if="modalErrorMessage" class="modal-error-message">
            {{ modalErrorMessage }}
          </div>

          <div class="system-info-actions">
            <button 
              class="edit-button" 
              @click="editEstimatedBill"
              :disabled="isLoading"
            >
              Edit / Go Back
            </button>
            <button 
              class="confirm-button" 
              @click="proceedWithEstimatedBill"
              :disabled="isLoading"
            >
              <span v-if="isLoading" class="loading-spinner"></span>
              {{ isLoading ? 'Calculating...' : 'Confirm & Calculate Savings' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Results Section -->
      <div v-if="showResults" class="results-section">
        <div class="results-header">
          <h3>Your Smart Savings Analysis with Renox AI</h3>
          <p>Here's how much Renox AI could save you by optimising when you use, store, or sell your solar power.</p>
        </div>

        <!-- Period Toggle -->
        <div class="period-toggle-container">
          <button 
            class="period-toggle-btn" 
            :class="{ active: selectedPeriod === 'monthly' }"
            @click="selectedPeriod = 'monthly'"
          >
            Monthly
          </button>
          <button 
            class="period-toggle-btn" 
            :class="{ active: selectedPeriod === 'quarterly' }"
            @click="selectedPeriod = 'quarterly'"
          >
            Quarterly
          </button>
          <button 
            class="period-toggle-btn" 
            :class="{ active: selectedPeriod === 'annual' }"
            @click="selectedPeriod = 'annual'"
          >
            Annual
          </button>
        </div>

        <div class="results-grid">
          <!-- Savings Cards - Show only selected period -->
          <div v-if="selectedPeriod === 'monthly'" class="savings-card monthly">
            <div class="card-header">
              <h4>Monthly Savings</h4>
              <div class="card-icon">📅</div>
            </div>
            <div class="card-value">
              ${{ formatCurrency(savingsData.monthly_savings) }}
            </div>
            <div class="card-description">Your potential monthly energy savings</div>
          </div>

          <div v-if="selectedPeriod === 'quarterly'" class="savings-card quarterly">
            <div class="card-header">
              <h4>Quarterly Savings</h4>
              <div class="card-icon">📊</div>
            </div>
            <div class="card-value">
              ${{ formatCurrency(savingsData.quarterly_savings) }}
            </div>
            <div class="card-description">Your potential quarterly energy savings</div>
          </div>

          <div v-if="selectedPeriod === 'annual'" class="savings-card annual">
            <div class="card-header">
              <h4>Annual Savings</h4>
              <div class="card-icon">💰</div>
            </div>
            <div class="card-value">
              ${{ formatCurrency(savingsData.annual_savings) }}
            </div>
            <div class="card-description">Your potential yearly energy savings</div>
          </div>

          <!-- Rate Information Cards -->
          <div class="rate-card">
            <div class="card-header">
              <h4>Average Usage Rate</h4>
              <div class="card-icon">⚡</div>
            </div>
            <div class="card-value">
              ${{ formatCurrency(savingsData.average_usage_rate) }}/kWh
            </div>
            <div class="card-description">Average electricity purchase rate</div>
          </div>

          <div class="rate-card">
            <div class="card-header">
              <h4>Average Feed-in Rate</h4>
              <div class="card-icon">🔋</div>
            </div>
            <div class="card-value">
              ${{ formatCurrency(savingsData.average_feedin_rate) }}/kWh
            </div>
            <div class="card-description">Average solar feed-in tariff rate</div>
          </div>
        </div>

        <!-- Additional Information -->
        <div class="additional-info">
          <div class="info-card" :class="eligibilityClass">
            
            <div v-if="savingsData.is_eligible === 1" class="eligible-info-box">
              <h5>Your Battery is eligible for Renox AI</h5>
              <p>Great news! 🎉</p>
              <p>Your system is well-suited for AI optimisation.</p>
              <p><strong>You can save even more with Renox AI, up to ${{ formatCurrency(savingsData.annual_savings) }} per year by intelligently optimising your energy flow and battery usage.</strong></p>
            </div>
            <div v-else class="eligible-info-box"> 
              <h5>Your battery may not be eligible for Renox AI integration</h5>
              <p>Because of your current energy consumption and system setup, <strong>Renox AI may not deliver significant savings at this stage. </strong></p>
              <p>Our team can review your setup and help identify ways to improve efficiency and unlock better results in the future.<br></p>
            </div>
          </div>
        </div>

        <!-- Calculation Factors Section -->
        <div class="calculation-factors">
                
          <div class="factors-list">

            <div class="factor-item">
              <span class="factor-title">How We Calculate Your Savings</span>
              <span class="factor-description">Renox AI calculates your potential savings by analysing a range of real performance factors unique to your system and location. We consider your battery size, which determines how much stored energy can be optimised, and your inverter capacity, which affects how efficiently your system manages the energy flow between solar, battery, and grid. Your solar panel capacity and solar exposure are also key. we use location-specific sunlight and weather data to estimate your true solar generation potential. In addition, we study your energy consumption patterns to understand how and when you use electricity, helping us predict smarter optimisation opportunities. We also take into account your state-based electricity rates and regulations to ensure accurate local pricing. Finally, Renox AI draws on real-world data from similar systems in your area, using proven savings results from other customers to refine your personalised savings projection. Estimated average rates and feed-in rate with Renox AI are calculated using historical market prices in your area, along with your battery capacity, solar system size, energy usage, and consumption patterns. </span>
            </div>

            <div class="factor-item">
              <a href="https://renox.tech/renox-ai/" target="_blank">Learn more about the Renox AI energy plan</a>
              <a href="https://renox.tech/localvolts-wholesale-market/" target="_blank">Learn more about the wholesale energy market</a>
            </div>
            
            
            
          </div>
        </div>
      </div>

      <!-- Error Section -->
      <div v-if="errorMessage" class="error-section">
        <div class="error-card">
          <div class="error-icon">⚠️</div>
          <h4>Error</h4>
          <p>{{ errorMessage }}</p>
          <button class="retry-button" @click="calculateSavings">Try Again</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiRequest } from '@/services/apiService';
import { getDevModeState } from '@/services/cookieService';

export default {
  name: 'AiOnboardingEvaluatorPage',
  data() {
    return {
      isLoading: false,
      showResults: false,
      showSystemInfo: false,
      showEstimatedBill: false,
      errorMessage: '',
      subDataUrl: "/api/v1.1/data",
      // Plant selection data
      plantSearchInput: '',
      plantList: [],
      selectedPlant: null,
      selectedPlantIndex: -1,
      showPlantDropdown: false,
      isSearchingPlants: false,
      plantSearchError: '',
      searchTimeout: null,
      systemInfo: null,
      pvSizeUserProvided: '',
      pvInverterSizeUserProvided: '',
      batterySizeUserProvided: '',
      dailyConsumptionUserProvided: '',
      estimatedBillUserProvided: '',
      modalErrorMessage: '',
      savingsData: {
        monthly_savings: 0,
        quarterly_savings: 0,
        annual_savings: 0,
        average_usage_rate: 0,
        average_feedin_rate: 0,
        is_eligible: 0
      },
      selectedPeriod: 'annual' // Default to annual
    };
  },
  computed: {
    eligibilityClass() {
      return this.savingsData.is_eligible === 1 ? 'eligible' : 'not-eligible';
    },
    eligibilityText() {
      return this.savingsData.is_eligible === 1 ? 'Eligible' : 'Not Eligible';
    }
  },
  methods: {
    readFromCookies() {
      // Initialize subDataUrl based on dev mode state from cookies
      const devModeState = getDevModeState();
      if (devModeState.isDevMode !== undefined) {
        this.subDataUrl = devModeState.isDevMode ? "/dev-api/api/v1.1/data" : "/api/v1.1/data";
      }
    },
    async getPlantByAutocomplete(inputValue) {
      this.selectedPlantIndex = -1;
      if (inputValue.trim() === '' || inputValue.length < 3) {
        this.plantList = [];
        return;
      }
      try {
        this.isSearchingPlants = true;
        this.plantSearchError = '';
        const bodyData = {
          input_text: inputValue,
        };
        const url = `${this.subDataUrl}/bridge/rx_data/autocomplete/plant_info/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data) {
          this.plantList = response.data;
        } else {
          this.plantSearchError = 'Failed to fetch plant data. Please try again.';
          this.plantList = [];
        }
      } catch (error) {
        console.error('Error fetching plant data:', error);
        this.plantSearchError = 'Failed to fetch plant data. Please try again.';
        this.plantList = [];
      } finally {
        this.isSearchingPlants = false;
      }
    },
    onPlantSearchInput() {
      clearTimeout(this.searchTimeout);
      this.plantSearchError = '';
      
      if (this.plantSearchInput.length >= 3) {
        this.searchTimeout = setTimeout(() => {
          this.getPlantByAutocomplete(this.plantSearchInput);
        }, 300);
      } else {
        this.plantList = [];
      }
    },
    onPlantSearchKeydown(event) {
      if (this.plantList.length === 0) return;
      
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedPlantIndex = Math.min(this.selectedPlantIndex + 1, this.plantList.length - 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.selectedPlantIndex = Math.max(this.selectedPlantIndex - 1, -1);
          break;
        case 'Enter':
          event.preventDefault();
          if (this.selectedPlantIndex >= 0) {
            this.selectPlant(this.plantList[this.selectedPlantIndex]);
          }
          break;
        case 'Escape':
          this.hidePlantDropdown();
          break;
      }
    },
    selectPlant(plant) {
      this.selectedPlant = plant;
      this.plantSearchInput = plant.plant_name;
      this.showPlantDropdown = false;
      this.selectedPlantIndex = -1;
      this.plantSearchError = '';
    },
    clearSelectedPlant() {
      this.selectedPlant = null;
      this.plantSearchInput = '';
      this.plantList = [];
      this.showPlantDropdown = false;
      this.selectedPlantIndex = -1;
      this.plantSearchError = '';
      this.showResults = false;
      this.showSystemInfo = false;
      this.showEstimatedBill = false;
      this.systemInfo = null;
      this.pvSizeUserProvided = '';
      this.estimatedBillUserProvided = '';
      this.modalErrorMessage = '';
      this.errorMessage = '';
    },
    hidePlantDropdown() {
      // Delay hiding to allow click events to fire
      setTimeout(() => {
        this.showPlantDropdown = false;
      }, 200);
    },
    async calculateSavings() {
      if (!this.selectedPlant) {
        this.errorMessage = 'Please enter the address.';
        return;
      }

      this.isLoading = true;
      this.showResults = false;
      this.showSystemInfo = false;
      this.showEstimatedBill = false;
      this.errorMessage = '';
      this.pvSizeUserProvided = '';
      this.estimatedBillUserProvided = '';
      this.modalErrorMessage = '';

      try {
        const bodyData = {
          plant_id: this.selectedPlant.plant_id
        };
        const url = `${this.subDataUrl}/ai_onboarding/plant_system_info/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data) {
          this.systemInfo = response.data;
          // Initialize user-provided values from systemInfo, rounded to 2 decimal places
          this.pvSizeUserProvided = this.systemInfo.estimated_pv_array_size_kw ? parseFloat(this.systemInfo.estimated_pv_array_size_kw).toFixed(2) : '';
          this.showSystemInfo = true;
        } else {
          this.errorMessage = response.infoText || 'Failed to retrieve system information. Please try again.';
        }
      } catch (error) {
        console.error('Error fetching system info:', error);
        this.errorMessage = 'An error occurred while fetching system information. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },
    async proceedWithCalculation() {
      if (!this.selectedPlant) {
        this.errorMessage = 'Please select a plant first.';
        return;
      }

      this.isLoading = true;
      this.showResults = false;
      this.showEstimatedBill = false;
      this.errorMessage = '';
      this.modalErrorMessage = '';

      try {
        const bodyData = {
          plant_id: this.selectedPlant.plant_id
        };
        
        // Add user-provided values if they have been edited (different from original or explicitly entered)
        if (this.pvSizeUserProvided && parseFloat(this.pvSizeUserProvided) > 0) {
          bodyData.pv_size_user_provided = parseFloat(this.pvSizeUserProvided);
        }
        
        const url = `${this.subDataUrl}/ai_onboarding/estimated_bill/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data) {
          // Pre-fill the estimated bill field with the API response value
          this.estimatedBillUserProvided = response.data.estimated_monthly_bill ? parseFloat(response.data.estimated_monthly_bill).toFixed(2) : '';
          this.showEstimatedBill = true;
          this.showSystemInfo = false;
        } else {
          this.errorMessage = response.infoText || 'Failed to retrieve estimated bill data. Please try again.';
        }
      } catch (error) {
        console.error('Error fetching estimated bill:', error);
        this.errorMessage = 'An error occurred while fetching your estimated bill. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },
    async proceedWithEstimatedBill() {
      if (!this.selectedPlant) {
        this.errorMessage = 'Please select a plant first.';
        return;
      }

      this.isLoading = true;
      this.showResults = false;
      this.errorMessage = '';
      this.modalErrorMessage = '';

      try {
        const bodyData = {
          plant_id: this.selectedPlant.plant_id
        };
        
        // Add user-provided values if they have been edited (different from original or explicitly entered)
        if (this.pvSizeUserProvided && parseFloat(this.pvSizeUserProvided) > 0) {
          bodyData.pv_size_user_provided = parseFloat(this.pvSizeUserProvided);
        }
        
        if (this.estimatedBillUserProvided && parseFloat(this.estimatedBillUserProvided) > 0) {
          bodyData.estimated_bill_user_provided = parseFloat(this.estimatedBillUserProvided);
        }
        
        const url = `${this.subDataUrl}/ai_onboarding/check_eligibility/get`;
        const response = await apiRequest('post', url, bodyData);
        
        if (response.errorCode === 0 && response.data) {
          this.savingsData = {
            monthly_savings: response.data.monthly_savings || 0,
            quarterly_savings: response.data.quarterly_savings || 0,
            annual_savings: response.data.annual_savings || 0,
            average_usage_rate: response.data.average_usage_rate || 0,
            average_feedin_rate: response.data.average_feedin_rate || 0,
            is_eligible: response.data.is_eligible || 0
          };
          this.showResults = true;
          this.showEstimatedBill = false;
        } else {
          this.errorMessage = response.infoText || 'Failed to retrieve savings data. Please try again.';
        }
      } catch (error) {
        console.error('Error calculating savings:', error);
        this.errorMessage = 'An error occurred while calculating your potential savings. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },
    editSystemInfo() {
      this.showSystemInfo = false;
      this.systemInfo = null;
      this.pvSizeUserProvided = '';
      this.modalErrorMessage = '';
    },
    editEstimatedBill() {
      this.showEstimatedBill = false;
      this.estimatedBillUserProvided = '';
      this.modalErrorMessage = '';
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '0.00';
      return parseFloat(value).toFixed(2);
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '0.0';
      return parseFloat(value).toFixed(1);
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      try {
        const date = new Date(dateTimeString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateTimeString;
      }
    }
  },
  created() {
    this.readFromCookies();
  }
};
</script>

<style scoped>
.page-container {
  width: 100%;
  padding: 2rem;
  background: var(--bg-primary);
  min-height: 100vh;
}

.header-section {
  text-align: center;
  margin-bottom: 3rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 1rem;
}

.page-description {
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.plant-selection-section {
  display: flex;
  justify-content: center;
}

.plant-selection-card {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 4px 20px var(--shadow-color);
  padding: 2rem;
  max-width: 900px;
  width: 100%;
  border: 1px solid var(--border-color);
}

.plant-header h5 {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.plant-header p {
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
}

.plant-search-container {
  position: relative;
  margin-bottom: 1rem;
}

.search-input-wrapper {
  position: relative;
}

.plant-search-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  padding-right: 3rem;
  background: var(--input-bg);
  color: var(--text-primary);
}

.plant-search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.plant-search-input.error {
  border-color: #dc3545;
}

.search-spinner {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.plant-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 4px 12px var(--shadow-color);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 4px;
}

.plant-option {
  padding: 1rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.2s ease;
}

.plant-option:hover,
.plant-option.highlighted {
  background-color: var(--bg-secondary);
}

.plant-option:last-child {
  border-bottom: none;
}

.plant-info {
  flex: 1;
}

.plant-name {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
}

.plant-account {
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.plant-id {
  font-size: 0.8rem;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.no-results {
  padding: 1rem;
  text-align: center;
  color: var(--text-tertiary);
  font-style: italic;
}

.selected-plant {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
}

.selected-plant-info h5 {
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  font-size: 1rem;
}

.plant-details {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.plant-details .plant-name {
  font-weight: 600;
  color: var(--text-primary);
}

.plant-details .plant-account {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.plant-details .plant-id {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
}

.clear-plant-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}

.clear-plant-btn:hover {
  background: #c82333;
}

.search-error {
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
}

.selection-required {
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  text-align: center;
  font-style: italic;
}

.calculate-button-container {
  margin-top: 1.5rem;
  text-align: center;
}

.calculate-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 250px;
  margin: 0 auto;
  width: 100%;
}

.calculate-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.calculate-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff40;
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
  overflow-y: auto;
}

.system-info-modal {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 10px 40px var(--shadow-color);
  padding: 2rem;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: modalSlideIn 0.3s ease-out;
  border: 1px solid var(--border-color);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1;
}

.modal-close:hover {
  background: #c82333;
  transform: rotate(90deg);
}

.system-info-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
}

.system-info-header h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.system-info-header p {
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.6;
}

.info-message-container {
  margin-top: 1rem;
  padding-top: 1rem;
}

.info-message-divider {
  border-top: 1px solid var(--border-color);
  margin-bottom: 1rem;
}

.system-info-header .info-message-for-user {
  color: #ff9800 !important;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  font-weight: 500;
}

.system-info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.info-item {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid #667eea;
  transition: transform 0.2s ease;
  border: 1px solid var(--border-color);
}

.info-item:hover {
  transform: translateY(-2px);
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 0.9rem;
  color: var(--text-tertiary);
  font-weight: 500;
  margin-bottom: 0.5rem;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
}

.info-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.info-input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  transition: all 0.3s ease;
  background: var(--input-bg);
  color: var(--text-primary);
}

.info-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.info-input:hover {
  border-color: #667eea;
}

.info-unit {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-secondary);
  white-space: nowrap;
}

.info-hint {
  font-size: 0.85rem;
  color: var(--text-tertiary);
  font-style: italic;
  margin-top: 0.25rem;
}

.user-input-section {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid var(--border-color);
  border-left: 4px solid #667eea;
}

.user-input-header {
  margin-bottom: 1rem;
}

.user-input-header h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.user-input-header p {
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
}

.user-input-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.user-input-field label {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-primary);
}

.pv-size-input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background: var(--input-bg);
  color: var(--text-primary);
}

.pv-size-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.pv-size-input.error {
  border-color: #dc3545;
}

.pv-size-input.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.modal-error-message {
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 4px;
}

.system-info-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
}

.edit-button,
.confirm-button {
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 200px;
}

.edit-button {
  background: #6c757d;
  color: white;
}

.edit-button:hover:not(:disabled) {
  background: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.confirm-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.confirm-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.edit-button:disabled,
.confirm-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.results-section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: 0 4px 20px var(--shadow-color);
  padding: 2rem;
  border: 1px solid var(--border-color);
}

.results-header {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
}

.results-header h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.period-toggle-container {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.period-toggle-btn {
  padding: 0.75rem 1.5rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
}

.period-toggle-btn:hover {
  border-color: #667eea;
  background: var(--bg-secondary);
  transform: translateY(-2px);
}

.period-toggle-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.eligibility-badge {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
}

.eligibility-badge.eligible {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.eligibility-badge.not-eligible {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
}

.savings-card, .rate-card {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid;
  transition: transform 0.2s ease;
  border: 1px solid var(--border-color);
}

.savings-card:hover, .rate-card:hover {
  transform: translateY(-2px);
}

.savings-card.monthly {
  border-left-color: #28a745;
}

.savings-card.quarterly {
  border-left-color: #17a2b8;
}

.savings-card.annual {
  border-left-color: #ffc107;
}

.rate-card {
  border-left-color: #6f42c1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.card-header h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.card-icon {
  font-size: 1.5rem;
}

.card-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.card-description {
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.4;
}

.additional-info {
  margin-top: 2rem;
}

.info-card {
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid;
}

.info-card.eligible {
  background: rgba(0, 191, 99, 0.3) !important;
  border-left-color: #00bf63 !important;
}

.info-card.not-eligible {
  background: rgba(255, 49, 49, 0.3) !important;
  border-left-color: #ff3131 !important;
}

.info-card h4,
.info-card h5 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text-primary);
}

.info-card.eligible h4,
.info-card.eligible h5 {
  color: #00884a;
}

.info-card.not-eligible h4,
.info-card.not-eligible h5 {
  color: #cc0000;
}

.info-card p {
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
}

.eligible-info-box p {
  font-size: 1.1rem;
}

.calculation-factors {
  margin-top: 2rem;
  background: var(--bg-secondary);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid var(--border-color);
}

.factors-header {
  text-align: center;
  margin-bottom: 2rem;
}

.factors-header h4 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.factors-header p {
  color: var(--text-secondary);
  font-size: 1rem;
  max-width: 100%;
  margin: 0 auto;
  line-height: 1.6;
}

.factors-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.factor-item {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: background-color 0.2s ease;
}

.factor-item:hover {
  background-color: var(--bg-secondary);
}

.factor-title {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
}

.factor-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
}

.factor-item a {
  color: var(--text-primary);
  text-decoration: underline;
}

.factor-item img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

.error-section {
  display: flex;
  justify-content: center;
}

.error-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  max-width: 500px;
  width: 100%;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.error-card h4 {
  color: var(--text-primary);
  font-size: 1.3rem;
  margin-bottom: 1rem;
}

.error-card p {
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
}

.retry-button {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

  .retry-button:hover {
    background: #c82333;
  }

@media (max-width: 1024px) {
  .system-info-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 1rem;
  }
  
  .page-title {
    font-size: 1.2rem;
  }
  
  .plant-selection-card,
  .action-card {
    padding: 1.5rem;
  }
  
  .plant-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  
  .selected-plant {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .clear-plant-btn {
    align-self: flex-end;
  }
  
  .modal-overlay {
    padding: 0.5rem;
  }
  
  .system-info-modal {
    padding: 1.5rem;
    max-height: 95vh;
  }
  
  .modal-close {
    width: 35px;
    height: 35px;
    font-size: 1.25rem;
  }
  
  .system-info-grid {
    grid-template-columns: 1fr;
  }
  
  .system-info-actions {
    flex-direction: column;
  }
  
  .edit-button,
  .confirm-button {
    width: 100%;
    min-width: unset;
  }
  
  .results-grid {
    grid-template-columns: 1fr;
  }
  
  .results-header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .period-toggle-container {
    gap: 0.5rem;
  }
  
  .period-toggle-btn {
    min-width: 100px;
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
  }
  
  .calculate-button {
    min-width: 200px;
    padding: 0.875rem 1.5rem;
  }
  
  .calculation-factors {
    padding: 1.5rem;
  }
  
  .factor-item {
    padding: 1rem;
  }
  
  .factor-title {
    font-size: 0.95rem;
  }
  
  .factor-description {
    font-size: 0.85rem;
  }
}
</style>
